import type { ZwCopyrightPage, ZwMetadata } from "../lib/zwConfig";
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { escapeHtmlText } from "./bookTemplate";
import {
  buildCopyrightPageContent,
  type CopyrightParagraph,
} from "./copyrightPageContent";

export {
  buildCopyrightPageContent,
  type CopyrightParagraph,
  type CopyrightParagraphKind,
} from "./copyrightPageContent";

function paragraphHtml(para: CopyrightParagraph): string {
  const text = escapeHtmlText(para.text);
  switch (para.kind) {
    case "notice":
      return `<p class="copyright-notice">${text}</p>`;
    case "credit":
      return `<p class="copyright-credit">${text}</p>`;
    case "edition":
      return `<p class="copyright-edition">${text}</p>`;
    case "url": {
      const href = /^https?:\/\//i.test(para.text)
        ? para.text
        : `https://${para.text}`;
      return `<p class="copyright-url"><a href="${escapeHtmlText(href)}">${text}</a></p>`;
    }
    case "imprint":
      if (para.text.startsWith("ISBN ")) {
        return `<p class="copyright-isbn">${text}</p>`;
      }
      if (para.text.startsWith("Library of Congress")) {
        return `<p class="copyright-lccn">${text}</p>`;
      }
      return `<p>${text}</p>`;
    case "body":
      return `<p>${text}</p>`;
    default:
      return `<p>${text}</p>`;
  }
}

/**
 * Build the copyright page HTML, or Nothing when the page is disabled / empty.
 */
export function buildCopyrightPageHtml(
  page: ZwCopyrightPage,
  meta: ZwMetadata,
): Maybe<string> {
  const content = buildCopyrightPageContent(page, meta);
  if (isJust(content) === false) return nothing;

  const blocks: string[] = [];
  let credits: string[] = [];
  let imprint: string[] = [];

  const flushCredits = (): void => {
    if (credits.length === 0) return;
    blocks.push(
      `<div class="copyright-credits">\n${credits.join("\n")}\n</div>`,
    );
    credits = [];
  };
  const flushImprint = (): void => {
    if (imprint.length === 0) return;
    blocks.push(
      `<div class="copyright-imprint">\n${imprint.join("\n")}\n</div>`,
    );
    imprint = [];
  };

  for (const para of content.value) {
    if (para.kind === "credit") {
      flushImprint();
      credits.push(paragraphHtml(para));
      continue;
    }
    if (para.kind === "imprint" || para.kind === "url") {
      flushCredits();
      imprint.push(paragraphHtml(para));
      continue;
    }
    flushCredits();
    flushImprint();
    blocks.push(paragraphHtml(para));
  }
  flushCredits();
  flushImprint();

  if (blocks.length === 0) return nothing;
  return just(`<div class="copyright-page">\n${blocks.join("\n")}\n</div>`);
}
